import React, { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Copy, Download, Share2, CheckCircle2 } from 'lucide-react'
import { Button } from './ui/Button'
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from './ui/Tooltip'
import { Separator } from './ui/Separator'

const ICON_BUTTON_CLASS =
    'tw-h-7 tw-w-7 tw-text-neutral-400 dark:tw-text-neutral-500 hover:tw-text-neutral-700 dark:hover:tw-text-neutral-200 hover:tw-bg-neutral-100 dark:hover:tw-bg-neutral-700/60 disabled:tw-opacity-30'

const ICON_ANIM = {
    initial: { scale: 0.5, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.5, opacity: 0 },
    transition: { duration: 0.15 },
}

export function FooterActions({ notesPlain, onCopy, onDownload, onGenerateGraph }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = useCallback(async () => {
        if (!notesPlain) return
        await onCopy()
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [notesPlain, onCopy])

    return (
        <div className="tw-flex-shrink-0">
            <Separator className="tw-bg-neutral-200 dark:tw-bg-neutral-800 tw-mb-2.5" />

            <div className="tw-flex tw-items-center tw-justify-between">
                <TooltipProvider delayDuration={300}>
                    <div className="tw-flex tw-items-center tw-gap-1">
                        {/* Copy button */}
                        <Tooltip>
                            <TooltipTrigger>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={handleCopy}
                                    disabled={!notesPlain}
                                    className={ICON_BUTTON_CLASS}
                                >
                                    <AnimatePresence mode="wait">
                                        {copied ? (
                                            <motion.div key="check" {...ICON_ANIM}>
                                                <CheckCircle2 className="tw-w-3.5 tw-h-3.5 tw-text-neutral-600 dark:tw-text-neutral-300" />
                                            </motion.div>
                                        ) : (
                                            <motion.div key="copy" {...ICON_ANIM}>
                                                <Copy className="tw-w-3.5 tw-h-3.5" />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                {copied ? 'Copied!' : 'Copy to clipboard'}
                            </TooltipContent>
                        </Tooltip>

                        {/* Download button */}
                        <Tooltip>
                            <TooltipTrigger>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={onDownload}
                                    disabled={!notesPlain}
                                    className={ICON_BUTTON_CLASS}
                                >
                                    <Download className="tw-w-3.5 tw-h-3.5" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                                Download as .txt
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>

                {/* Generate graph action */}
                <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <Button
                        onClick={onGenerateGraph}
                        className="
                            tw-h-8 tw-px-4 tw-text-xs tw-font-medium
                            tw-bg-neutral-800 dark:tw-bg-neutral-700
                            hover:tw-bg-neutral-700 dark:hover:tw-bg-neutral-600
                            tw-text-white dark:tw-text-neutral-200 tw-border-0
                            tw-shadow-lg tw-shadow-black/10 dark:tw-shadow-black/30
                            tw-transition-all tw-duration-200
                        "
                    >
                        <Share2 className="tw-w-3.5 tw-h-3.5 tw-mr-1.5" />
                        Generate Graph
                    </Button>
                </motion.div>
            </div>
        </div>
    )
}
